/**
 * API Keys Management View
 * Allows global admins to manage credentials for predefined external services.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { toast } from 'sonner';
import { formatDate } from '@/utils/time';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Bug,
  Cloud,
  Globe,
  Archive,
  Mail,
  Key,
  Settings,
  Trash2,
  CheckCircle2,
  XCircle,
  Loader2,
  FlaskConical,
  ExternalLink,
  AlertCircle,
  BookOpen,
  Copy,
  Check,
} from 'lucide-react';

interface CredentialField {
  id: string;
  label: string;
  description: string;
  is_required: boolean;
  is_secret: boolean;
  placeholder: string;
}

interface ServiceDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  credentials: CredentialField[];
  docs_url: string | null;
  is_enabled: boolean;
}

interface CredentialStatus {
  is_set: boolean;
  masked_value: string | null;
  is_active: boolean;
}

interface ServiceStatus {
  service: ServiceDefinition;
  is_configured: boolean;
  is_active: boolean;
  credentials: Record<string, CredentialStatus>;
  updated_at: string | null;
  console_url?: string;
}

// Icon mapping
const iconMap: Record<string, React.ElementType> = {
  Cloud: Cloud,
  Globe: Globe,
  Archive: Archive,
  Mail: Mail,
  Key: Key,
  Bug: Bug,
};

function getServiceIcon(iconName: string) {
  return iconMap[iconName] || Key;
}

// Inline setup guides for services that need detailed configuration instructions
interface SetupStep {
  title: string;
  description: string;
  code?: string;
  codeLabel?: string;
}

const SERVICE_SETUP_GUIDES: Record<string, SetupStep[]> = {
  smtp: [
    {
      title: "1. Choose an SMTP provider",
      description: "You can use any SMTP-capable email provider. Common options: Gmail (with App Passwords), SendGrid, Amazon SES, Mailgun, or your own mail server.",
    },
    {
      title: "2. Gmail with App Passwords",
      description: "If using Gmail: Go to Google Account → Security → 2-Step Verification → App passwords. Generate an app password for 'Mail'. Use smtp.gmail.com, port 587, your Gmail address as username, and the generated app password.",
    },
    {
      title: "3. SendGrid",
      description: "If using SendGrid: Go to Settings → API Keys → Create API Key (Full Access or restricted to Mail Send). Use smtp.sendgrid.net, port 587, 'apikey' as username, and your API key as password.",
    },
    {
      title: "4. Configure above",
      description: "Click Configure, enter your SMTP details, and use the Test button to verify the connection. On save, the SMTP settings are automatically synced to Keycloak, enabling password reset emails and user invitations.",
    },
  ],
  hetzner: [
    {
      title: "1. Create a Hetzner Cloud project",
      description: "Go to Hetzner Cloud Console and create a dedicated project (e.g. \"AOC Customer Servers\"). Keeping a separate project isolates customer servers from other infrastructure.",
    },
    {
      title: "2. Generate an API token",
      description: "In your project, go to Security → API Tokens → Generate API Token. Select Read & Write permissions. Copy the token immediately — it won't be shown again.",
    },
    {
      title: "3. Paste the token above",
      description: "Click Configure, paste the API token, and use the Test button to verify it works. The test will list available server types to confirm read access.",
    },
  ],
  route53: [
    {
      title: "1. Find your bswn.io hosted zone ID",
      description: "In the AWS Console, go to Route 53 → Hosted zones → bswn.io. Copy the Hosted zone ID (e.g. Z1234567890ABC).",
    },
    {
      title: "2. Create a dedicated IAM user",
      description: "Go to IAM → Users → Create user (e.g. \"aoc-route53\"). Do not enable console access. Attach the following inline policy, replacing ZONE_ID_FOR_BSWN_IO with your actual hosted zone ID:",
      codeLabel: "IAM Policy",
      code: JSON.stringify({
        "Version": "2012-10-17",
        "Statement": [
          {
            "Effect": "Allow",
            "Action": [
              "route53:ChangeResourceRecordSets",
              "route53:ListResourceRecordSets"
            ],
            "Resource": "arn:aws:route53:::hostedzone/ZONE_ID_FOR_BSWN_IO"
          },
          {
            "Effect": "Allow",
            "Action": [
              "route53:ListHostedZones",
              "route53:GetHostedZone"
            ],
            "Resource": "*"
          }
        ]
      }, null, 2),
    },
    {
      title: "3. Generate access keys",
      description: "On the IAM user page, go to Security credentials → Access keys → Create access key. Select \"Third-party service\" and confirm. Copy both the Access Key ID and Secret Access Key.",
    },
    {
      title: "4. Configure above",
      description: "Click Configure, paste both keys, and use the Test button to verify. The test will confirm Route53 access by listing hosted zones.",
    },
  ],
  s3_ssh_archive: [
    {
      title: "1. Create an S3 bucket",
      description: "In the AWS Console, go to S3 → Create bucket (e.g. \"aoc-ssh-keys-archive\"). Enable Versioning under Bucket Versioning. Keep all other defaults (Block all public access should stay ON).",
    },
    {
      title: "2. Create a write-only IAM user",
      description: "Go to IAM → Users → Create user (e.g. \"aoc-s3-writer\"). Attach the following inline policy. This grants only PutObject and explicitly denies all read operations — the SSH keys can be archived but never retrieved through AOC:",
      codeLabel: "IAM Policy",
      code: JSON.stringify({
        "Version": "2012-10-17",
        "Statement": [
          {
            "Effect": "Allow",
            "Action": ["s3:PutObject"],
            "Resource": "arn:aws:s3:::BUCKET_NAME/ssh-keys/*"
          },
          {
            "Effect": "Deny",
            "Action": [
              "s3:GetObject",
              "s3:ListBucket",
              "s3:GetBucketLocation"
            ],
            "Resource": [
              "arn:aws:s3:::BUCKET_NAME",
              "arn:aws:s3:::BUCKET_NAME/*"
            ]
          }
        ]
      }, null, 2),
    },
    {
      title: "3. Add a bucket policy (optional, extra safety)",
      description: "On the bucket, go to Permissions → Bucket policy. Add a policy that denies reads from the aoc-s3-writer user as a second layer of protection:",
      codeLabel: "Bucket Policy",
      code: JSON.stringify({
        "Version": "2012-10-17",
        "Statement": [
          {
            "Effect": "Deny",
            "Principal": { "AWS": "arn:aws:iam::ACCOUNT_ID:user/aoc-s3-writer" },
            "Action": ["s3:GetObject", "s3:ListBucket"],
            "Resource": [
              "arn:aws:s3:::BUCKET_NAME",
              "arn:aws:s3:::BUCKET_NAME/*"
            ]
          }
        ]
      }, null, 2),
    },
    {
      title: "4. Generate access keys",
      description: "On the IAM user page, go to Security credentials → Access keys → Create access key. Copy the Access Key ID and Secret Access Key.",
    },
    {
      title: "5. Configure above",
      description: "Click Configure, enter both keys plus the bucket name and AWS region. Use the Test button to verify — it will attempt a small test write to the bucket.",
    },
  ],
};

function CodeBlock({ code, label }: { code: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-2 rounded-md border bg-muted/50">
      {label && (
        <div className="flex items-center justify-between border-b px-3 py-1.5">
          <span className="text-xs font-medium text-muted-foreground">{label}</span>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
            {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
          </Button>
        </div>
      )}
      <pre className="overflow-x-auto p-3 text-xs leading-relaxed"><code>{code}</code></pre>
    </div>
  );
}

function CredentialValue({ label, value, canCopy }: { label: string; value: string; canCopy: boolean }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-muted-foreground">{label}:</span>
      <div className="flex items-center gap-1">
        <code className="bg-muted px-2 py-1 rounded text-xs">{value}</code>
        {canCopy && (
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy} title="Copy to clipboard">
            {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
          </Button>
        )}
      </div>
    </div>
  );
}

export function ApiKeysView() {
  const queryClient = useQueryClient();
  const [configuringService, setConfiguringService] = useState<ServiceStatus | null>(null);
  const [deletingService, setDeletingService] = useState<ServiceStatus | null>(null);
  const [credentialValues, setCredentialValues] = useState<Record<string, string>>({});
  const [isActive, setIsActive] = useState(true);
  const [testedServices, setTestedServices] = useState<Record<string, boolean>>({});
  const [testingServiceId, setTestingServiceId] = useState<string | null>(null);

  // Fetch service configurations
  const { data: services = [], isLoading } = useQuery<ServiceStatus[]>({
    queryKey: ['service-credentials'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const response = await api.get('/global-admin/api-keys/');
      return response.data;
    },
  });

  // Save credentials mutation
  const saveMutation = useMutation({
    mutationFn: async (data: { service_id: string; credentials: Record<string, string>; is_active: boolean }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post('/global-admin/api-keys/save/', data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-credentials'] });
      setConfiguringService(null);
      setCredentialValues({});
      // Reset test status when credentials change
      setTestedServices(prev => {
        const updated = { ...prev };
        delete updated[variables.service_id];
        return updated;
      });
      toast.success('Credentials saved successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to save credentials: ' + (error.response?.data?.error || error.message));
    },
  });

  // Delete credentials mutation
  const deleteMutation = useMutation({
    mutationFn: async (serviceId: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/global-admin/api-keys/delete/${serviceId}/`);
    },
    onSuccess: (_, serviceId) => {
      queryClient.invalidateQueries({ queryKey: ['service-credentials'] });
      setDeletingService(null);
      // Reset test status when credentials are deleted
      setTestedServices(prev => {
        const updated = { ...prev };
        delete updated[serviceId];
        return updated;
      });
      toast.success('Credentials deleted successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to delete credentials: ' + (error.response?.data?.error || error.message));
    },
  });

  // Test credentials mutation
  const testMutation = useMutation({
    mutationFn: async (serviceId: string) => {
      setTestingServiceId(serviceId);
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/global-admin/api-keys/test/${serviceId}/`);
    },
    onSuccess: (response) => {
      const result = response.data;
      if (result.success === true) {
        toast.success(result.message);
        if (testingServiceId) {
          setTestedServices(prev => ({ ...prev, [testingServiceId]: true }));
        }
      } else if (result.success === false) {
        toast.error(result.message);
        if (testingServiceId) {
          setTestedServices(prev => ({ ...prev, [testingServiceId]: false }));
        }
      } else {
        toast.info(result.message);
      }
      setTestingServiceId(null);
    },
    onError: (error: any) => {
      toast.error('Failed to test credentials: ' + (error.response?.data?.error || error.message));
      if (testingServiceId) {
        setTestedServices(prev => ({ ...prev, [testingServiceId]: false }));
      }
      setTestingServiceId(null);
    },
  });

  // Toggle service active status
  const toggleMutation = useMutation({
    mutationFn: async ({ serviceId, isActive }: { serviceId: string; isActive: boolean }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/global-admin/api-keys/toggle/${serviceId}/`, { is_active: isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-credentials'] });
    },
    onError: (error: any) => {
      toast.error('Failed to toggle service: ' + (error.response?.data?.error || error.message));
    },
  });

  const handleConfigure = (serviceStatus: ServiceStatus) => {
    // Pre-fill with empty values
    const initialValues: Record<string, string> = {};
    serviceStatus.service.credentials.forEach(cred => {
      initialValues[cred.id] = '';
    });
    setCredentialValues(initialValues);
    setIsActive(serviceStatus.is_active || true);
    setConfiguringService(serviceStatus);
  };

  const handleSave = () => {
    if (!configuringService) return;
    saveMutation.mutate({
      service_id: configuringService.service.id,
      credentials: credentialValues,
      is_active: isActive,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4">
        {services.map((serviceStatus) => {
          const IconComponent = getServiceIcon(serviceStatus.service.icon);

          return (
            <Card key={serviceStatus.service.id} className={serviceStatus.is_configured ? '' : 'border-dashed'}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${serviceStatus.is_configured ? 'bg-primary/10' : 'bg-muted'}`}>
                      <IconComponent className={`h-6 w-6 ${serviceStatus.is_configured ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {serviceStatus.service.name}
                        {serviceStatus.is_configured ? (
                          serviceStatus.is_active ? (
                            <Badge variant="default" className="ml-2">
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="ml-2">
                              <XCircle className="h-3 w-3 mr-1" /> Inactive
                            </Badge>
                          )
                        ) : (
                          <Badge variant="outline" className="ml-2">
                            <AlertCircle className="h-3 w-3 mr-1" /> Not Configured
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {serviceStatus.service.description}
                      </CardDescription>
                      {serviceStatus.is_configured && serviceStatus.console_url && (
                        <a
                          href={serviceStatus.console_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                          data-testid={`console-link-${serviceStatus.service.id}`}
                        >
                          Open this instance&apos;s project
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {serviceStatus.is_configured && (
                      <>
                        <Switch
                          checked={serviceStatus.is_active}
                          onCheckedChange={(checked) =>
                            toggleMutation.mutate({
                              serviceId: serviceStatus.service.id,
                              isActive: checked
                            })
                          }
                          disabled={toggleMutation.isPending}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => testMutation.mutate(serviceStatus.service.id)}
                          disabled={testMutation.isPending}
                          title="Test Connection"
                        >
                          {testMutation.isPending && testingServiceId === serviceStatus.service.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : testedServices[serviceStatus.service.id] === true ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          ) : testedServices[serviceStatus.service.id] === false ? (
                            <XCircle className="h-4 w-4 text-destructive" />
                          ) : (
                            <FlaskConical className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeletingService(serviceStatus)}
                          className="text-destructive hover:text-destructive"
                          title="Delete Credentials"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    <Button
                      variant={serviceStatus.is_configured ? "outline" : "default"}
                      onClick={() => handleConfigure(serviceStatus)}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      {serviceStatus.is_configured ? 'Update' : 'Configure'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {serviceStatus.is_configured && (
                  <div className="grid gap-2 text-sm">
                    {serviceStatus.service.credentials.map((cred) => {
                      const credStatus = serviceStatus.credentials[cred.id];
                      return (
                        <CredentialValue
                          key={cred.id}
                          label={cred.label}
                          value={credStatus?.masked_value || '(not set)'}
                          canCopy={!!credStatus?.masked_value}
                        />
                      );
                    })}
                    {serviceStatus.updated_at && (
                      <div className="text-xs text-muted-foreground mt-2">
                        Last updated: {formatDate(serviceStatus.updated_at)}
                      </div>
                    )}
                  </div>
                )}
                {SERVICE_SETUP_GUIDES[serviceStatus.service.id] && (
                  <Accordion type="single" collapsible className={serviceStatus.is_configured ? 'mt-3' : ''}>
                    <AccordionItem value="setup-guide" className="border-none">
                      <AccordionTrigger className="py-2 text-sm hover:no-underline">
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <BookOpen className="h-4 w-4" />
                          Setup guide
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 pt-1">
                          {SERVICE_SETUP_GUIDES[serviceStatus.service.id].map((step, i) => (
                            <div key={i}>
                              <h4 className="text-sm font-medium">{step.title}</h4>
                              <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
                              {step.code && (
                                <CodeBlock code={step.code} label={step.codeLabel} />
                              )}
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {services.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Key className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">No services available</p>
            <p className="text-muted-foreground text-center">
              Service integrations will appear here when they are configured in the system.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Configure Dialog */}
      <Dialog
        open={!!configuringService}
        onOpenChange={(open) => {
          if (!open) {
            setConfiguringService(null);
            setCredentialValues({});
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {configuringService && (
                <>
                  {(() => {
                    const IconComponent = getServiceIcon(configuringService.service.icon);
                    return <IconComponent className="h-5 w-5" />;
                  })()}
                  Configure {configuringService.service.name}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {configuringService?.is_configured
                ? 'Update the credentials for this service. Leave fields empty to keep existing values.'
                : 'Enter the required credentials to enable this service.'}
            </DialogDescription>
          </DialogHeader>

          {configuringService && (
            <div className="space-y-4">
              {configuringService.service.credentials.map((cred) => (
                <div key={cred.id} className="space-y-2">
                  <Label htmlFor={cred.id}>
                    {cred.label}
                    {cred.is_required && !configuringService.is_configured && (
                      <span className="text-destructive ml-1">*</span>
                    )}
                  </Label>
                  <Input
                    id={cred.id}
                    type={cred.is_secret ? 'password' : 'text'}
                    value={credentialValues[cred.id] || ''}
                    onChange={(e) => setCredentialValues({
                      ...credentialValues,
                      [cred.id]: e.target.value
                    })}
                    placeholder={
                      configuringService.is_configured
                        ? 'Leave empty to keep existing'
                        : cred.placeholder
                    }
                  />
                  <p className="text-xs text-muted-foreground">{cred.description}</p>
                </div>
              ))}

              <div className="flex items-center justify-between pt-2">
                <Label htmlFor="is_active">Enable service</Label>
                <Switch
                  id="is_active"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
              </div>

              {configuringService.service.docs_url && (
                <a
                  href={configuringService.service.docs_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  View documentation
                </a>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfiguringService(null);
                setCredentialValues({});
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingService} onOpenChange={(open) => !open && setDeletingService(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Credentials?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the credentials for "{deletingService?.service.name}".
              Any features depending on this service will stop working.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingService && deleteMutation.mutate(deletingService.service.id)}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
